import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ImagePlus, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/App';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import Footer from '@/components/Footer';

const MAX_PHOTOS = 3;
const MAX_BYTES = 4 * 1024 * 1024;

export default function BrandApply() {
  const darkMode = useDarkMode();
  const isDark = darkMode.isDark;
  const fileInputRef = useRef(null);

  useDocumentMeta({
    title: 'Apply to be featured — Mementa',
    description: 'Small business? Apply to be featured in the Mementa Support-a-Brand directory.',
    url: 'https://mementa.gifts/brands/apply',
  });

  const [form, setForm] = useState({
    brand_name: '',
    founder_name: '',
    email: '',
    website: '',
    category: '',
    story: '',
  });
  const [photos, setPhotos] = useState([]); // {file, preview}
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (files) => {
    const incoming = Array.from(files || []);
    const next = [...photos];
    for (const file of incoming) {
      if (next.length >= MAX_PHOTOS) {
        toast.error(`Maximum ${MAX_PHOTOS} photos`);
        break;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} is over 4MB`);
        continue;
      }
      next.push({ file, preview: URL.createObjectURL(file) });
    }
    setPhotos(next);
  };

  const removePhoto = (idx) => {
    URL.revokeObjectURL(photos[idx].preview);
    setPhotos(photos.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.brand_name.trim().length < 2) return toast.error('Brand name is required');
    if (form.founder_name.trim().length < 2) return toast.error('Founder name is required');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error('A valid email is required');
    if (form.story.trim().length < 20) return toast.error('Tell us a little more — your story needs at least 20 characters');
    if (photos.length === 0) return toast.error('Please add at least 1 photo');

    setSubmitting(true);
    try {
      // Upload photos one at a time (keeps each request small)
      const paths = [];
      for (let i = 0; i < photos.length; i += 1) {
        setProgress(`Uploading photo ${i + 1} of ${photos.length}…`);
        const fd = new FormData();
        fd.append('file', photos[i].file);
        const res = await api.post('/brands/upload-image', fd);
        paths.push(res.data.path);
      }
      setProgress('Submitting application…');
      await api.post('/brands/apply', { ...form, image_paths: paths });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong — please try again');
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  const inputStyle = {
    background: isDark ? '#1E2A36' : '#FFFFFF',
    border: `1px solid ${isDark ? 'rgba(212,164,168,0.35)' : 'rgba(83,65,69,0.25)'}`,
    color: isDark ? '#FFFAF5' : '#20212B',
  };
  const labelStyle = {
    color: isDark ? '#D4A4A8' : '#3F2F31',
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col page-enter" style={{ background: darkMode.bgPrimary }}>
        <div className="flex-1 flex items-center justify-center px-4 py-20">
          <div
            className="rounded-2xl p-10 sm:p-14 text-center max-w-lg"
            data-testid="brand-apply-success"
            style={{
              background: isDark ? 'rgba(212,164,168,0.1)' : 'rgba(83,65,69,0.05)',
              border: `1px solid ${isDark ? 'rgba(212,164,168,0.4)' : 'rgba(83,65,69,0.25)'}`,
            }}
          >
            <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: isDark ? '#D4A4A8' : '#534145' }} />
            <h1
              className="mb-3"
              style={{
                fontFamily: "'Bodoni Moda', serif",
                fontWeight: 700,
                fontSize: '26px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: isDark ? '#FFFAF5' : '#20212B',
              }}
            >
              Application received
            </h1>
            <p className="text-sm mb-6" style={{ color: isDark ? 'rgba(255,250,245,0.75)' : 'rgba(83,65,69,0.85)' }}>
              Thank you, {form.founder_name.split(' ')[0]}! We review every application by hand. You'll hear from us at {form.email} once {form.brand_name} has been reviewed.
            </p>
            <Link
              to="/brands"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
              style={{ background: isDark ? '#D4A4A8' : '#534145', color: isDark ? '#20212B' : '#FFFAF5' }}
            >
              Back to the directory
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-16">
        <Link
          to="/brands"
          className="inline-flex items-center gap-1.5 text-sm mb-8 transition-opacity hover:opacity-70"
          style={{ color: isDark ? '#D4A4A8' : '#9E6752' }}
        >
          <ArrowLeft size={15} /> Back to the directory
        </Link>

        <h1
          className="mb-3"
          style={{
            fontFamily: "'Bodoni Moda', serif",
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
            color: isDark ? '#FFFAF5' : '#20212B',
          }}
          data-testid="brand-apply-title"
        >
          Apply to be featured
        </h1>
        <p className="text-sm mb-10" style={{ fontFamily: 'Playfair Display, serif', color: isDark ? 'rgba(255,250,245,0.75)' : 'rgba(83,65,69,0.85)' }}>
          Tell us about your brand. We hand-review every application, and approved brands get their own page in the Mementa directory — free.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="brand-apply-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold mb-1.5" style={labelStyle}>Brand name *</label>
              <input value={form.brand_name} onChange={set('brand_name')} data-testid="brand-apply-name-input"
                className="w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="e.g. Willow & Clay Candle Co." />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold mb-1.5" style={labelStyle}>Your name *</label>
              <input value={form.founder_name} onChange={set('founder_name')} data-testid="brand-apply-founder-input"
                className="w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="Founder or owner" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold mb-1.5" style={labelStyle}>Email *</label>
              <input type="email" value={form.email} onChange={set('email')} data-testid="brand-apply-email-input"
                className="w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="you@yourbrand.com" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold mb-1.5" style={labelStyle}>Website / shop</label>
              <input value={form.website} onChange={set('website')} data-testid="brand-apply-website-input"
                className="w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="yourbrand.com" />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-bold mb-1.5" style={labelStyle}>What do you make?</label>
            <input value={form.category} onChange={set('category')} data-testid="brand-apply-category-input"
              className="w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="e.g. Hand-poured candles, ceramics, jewelry…" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-bold mb-1.5" style={labelStyle}>Your story *</label>
            <textarea value={form.story} onChange={set('story')} rows={5} data-testid="brand-apply-story-input"
              className="w-full rounded-lg px-4 py-2.5 text-sm outline-none resize-y" style={inputStyle}
              placeholder="Who are you, what do you make, and what makes it special? This appears on your brand page." />
          </div>

          {/* Photos */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold mb-1.5" style={labelStyle}>
              Photos * <span className="normal-case font-normal opacity-70">(1–3, max 4MB each)</span>
            </label>
            <div className="flex flex-wrap gap-3">
              {photos.map((p, i) => (
                <div key={p.preview} className="relative rounded-xl overflow-hidden" style={{ width: 104, height: 104 }}>
                  <img src={p.preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)} data-testid={`brand-apply-photo-remove-${i}`}
                    className="absolute top-1 right-1 rounded-full p-1"
                    style={{ background: 'rgba(0,0,0,0.65)', color: '#FFF' }}>
                    <X size={13} />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="brand-apply-photo-add"
                  className="rounded-xl flex flex-col items-center justify-center gap-1.5 transition-colors"
                  style={{
                    width: 104,
                    height: 104,
                    border: `2px dashed ${isDark ? 'rgba(212,164,168,0.5)' : 'rgba(83,65,69,0.35)'}`,
                    color: isDark ? '#D4A4A8' : '#534145',
                    background: 'transparent',
                  }}
                >
                  <ImagePlus size={20} />
                  <span className="text-[11px] font-medium">Add photo</span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              data-testid="brand-apply-file-input"
              onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="brand-apply-submit-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
            style={{ background: isDark ? '#D4A4A8' : '#534145', color: isDark ? '#20212B' : '#FFFAF5' }}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {progress || 'Submitting…'}
              </>
            ) : (
              'Submit application'
            )}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
}
